(() => {
  if (customElements.get('luxsol-roof-signup')) return;
  class LuxSolRoofSignup extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.slider = this.querySelector('.lsr-slider');
      this.roof = this.querySelector('.lsr-roof');
      this.status = this.querySelector('[data-status]');
      this.claim = this.querySelector('[data-claim]');
      this.form = this.querySelector('form');
      this.firstName = this.querySelector('[name="first_name"]');
      this.email = this.querySelector('[name="email"]');
      this.submit = this.querySelector('[data-submit]');
      this.error = this.querySelector('.lsr-error');
      this.teaser = this.querySelector('[data-open]');
      this.preview = this.dataset.preview === 'true';
      this.experimentId = 'roof_signup_20261006';
      this.signupVariant = this.chooseVariant();
      this.dataset.signupVariant = this.signupVariant;
      this.applyExperiment();
      this.unlocked = false;
      this.busy = false;
      this.currentStep = null;
      this.openSource = 'automatic';
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slider.addEventListener('input', () => this.updateRoof(), options);
      this.claim.addEventListener('click', () => {
        if (this.unlocked) this.showStep('email');
      }, options);
      this.querySelector('[data-back]').addEventListener('click', () => this.showStep('reveal'), options);
      this.querySelectorAll('[data-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), options));
      this.teaser.addEventListener('click', () => this.open('tab'), options);
      this.dialog.addEventListener('click', event => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        cancelAnimationFrame(this.roofFrame);
        if (this.currentStep !== 'success') this.track('luxsol_roof_close', { step: this.currentStep });
        if (!this.preview) this.save('dismissed', Date.now());
        this.teaser.hidden = !this.preview && Boolean(this.load('submitted'));
        this.firstName.value = '';
        this.email.value = '';
      }, options);
      this.form.addEventListener('submit', event => this.subscribe(event), options);
      if (this.signupVariant === 'direct') {
        this.unlocked = true;
        this.showStep('email', false);
      } else {
        this.updateRoof();
        this.showStep('reveal', false);
      }
      const excluded = /^\/(cart|checkout|checkouts|account)(\/|$)/.test(location.pathname);
      if (excluded) return;
      this.teaser.hidden = !this.preview && Boolean(this.load('submitted'));
      const force = this.preview && new URLSearchParams(location.search).get('luxsol_roof_preview') === '1';
      if (force) {
        this.open('preview');
      } else if (this.preview || (!this.load('submitted') && Date.now() - Number(this.load('dismissed') || 0) > 604800000)) {
        this.timer = setTimeout(() => this.open('automatic'), 20000);
      }
      this.addEventListener('luxsol:roof-open', () => this.open('external'), options);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.roofFrame);
      this.controller?.abort();
      this.requestController?.abort();
      this.currentStep = 'success';
      this.dialog?.close();
      this.ready = false;
    }
    chooseVariant() {
      if (this.preview && ['reveal', 'direct'].includes(this.dataset.abPreview)) return this.dataset.abPreview;
      const key = 'ab-' + this.experimentId;
      const saved = this.load(key);
      if (saved === 'reveal' || saved === 'direct') return saved;
      // Equal random allocation; persist the visitor's version across pages and visits.
      const variant = Math.random() < 0.5 ? 'reveal' : 'direct';
      this.save(key, variant);
      return variant;
    }
    applyExperiment() {
      const consent = this.querySelector('#lsr-consent');
      const privacy = consent.querySelector('a');
      consent.replaceChildren();
      const first = document.createElement('span');
      first.className = 'lsr-consent-line';
      first.textContent = 'By signing up, you agree to LuxSol marketing';
      const second = document.createElement('span');
      second.className = 'lsr-consent-line';
      second.append('emails. Unsubscribe anytime. ', privacy);
      consent.append(first, second);
      const style = document.createElement('style');
      style.textContent = `
        luxsol-roof-signup .lsr-dialog{container-type:inline-size}
        luxsol-roof-signup #lsr-consent{font-size:clamp(8px,3.5cqw,11px)}
        luxsol-roof-signup .lsr-consent-line{display:block;white-space:nowrap}
        luxsol-roof-signup[data-signup-variant="direct"] #lsr-title{white-space:nowrap;font-size:clamp(16px,7.3cqw,29px);letter-spacing:-.8px}
        luxsol-roof-signup[data-signup-variant="direct"] .lsr-roof{height:100px;margin-bottom:22px}
        luxsol-roof-signup[data-signup-variant="direct"] .lsr-reward strong{font-size:37px}
        luxsol-roof-signup[data-signup-variant="direct"] .lsr-input{text-align:left}
      `;
      this.append(style);
      if (this.signupVariant !== 'direct') return;
      this.querySelector('.lsr-kicker').textContent = 'Your welcome offer';
      this.querySelector('#lsr-title').textContent = '£100 off your pergola.';
      this.querySelector('#lsr-description').textContent = 'Sign up for £100 off any model/size';
      this.email.previousElementSibling.querySelectorAll('span')[1]?.remove();
      this.submit.dataset.idle = 'Get my £100 code →';
      this.submit.textContent = this.submit.dataset.idle;
    }
    animateDecorativeRoof() {
      cancelAnimationFrame(this.roofFrame);
      const started = performance.now();
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const paint = now => {
        const t = reduced ? 1 : Math.min(1, (now - started) / 1600);
        const value = (1 - Math.pow(1 - t, 3)) * 100;
        this.roof.style.setProperty('--lsr-angle', value * .88 + 'deg');
        this.roof.style.setProperty('--lsr-open', Math.max(0, (value - 28) / 72));
        const sunlight = Math.max(0, (value - 12) / 88);
        this.roof.style.setProperty('--lsr-sun', sunlight * sunlight);
        this.roof.style.setProperty('--lsr-sun-shift', value * .48 - 24 + 'px');
        if (t < 1 && this.dialog.open) this.roofFrame = requestAnimationFrame(paint);
      };
      this.roofFrame = requestAnimationFrame(paint);
    }
    load(key) {
      try { return localStorage.getItem('luxsol-roof-' + key); } catch { return null; }
    }
    save(key, value) {
      try { localStorage.setItem('luxsol-roof-' + key, String(value)); } catch {}
    }
    track(event, details = {}) {
      if (this.preview) return;
      const data = { form: 'roof_reveal_100', source: this.openSource, ...details, experiment_id: this.experimentId, signup_variant: this.signupVariant };
      try { window.Shopify?.analytics?.publish(event, data); } catch {}
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...data });
    }
    open(source = 'automatic') {
      // Never interrupt a mobile pergola configuration with an automatic offer.
      if (source === 'automatic' && matchMedia('(max-width:980px)').matches && document.querySelector('aura-builder')) return;
      if (this.dialog.open) return;
      if (!this.preview && this.load('submitted')) return;
      clearTimeout(this.timer);
      this.openSource = source;
      this.teaser.hidden = true;
      this.dialog.showModal();
      this.track('luxsol_roof_open');
      // Record the email step after exposure, so both versions have a valid event order.
      if (this.signupVariant === 'direct' && this.currentStep !== 'success') {
        this.showStep('email', false);
        this.track('luxsol_roof_email_step');
        this.animateDecorativeRoof();
      }
    }
    showStep(name, focus = true) {
      const previousStep = this.currentStep;
      this.currentStep = name;
      this.querySelectorAll('[data-step]').forEach(step => step.hidden = step.dataset.step !== name);
      this.querySelector('[data-intro]').hidden = name === 'success';
      this.roof.hidden = name === 'success';
      this.querySelector('[data-footer]').hidden = name === 'success';
      this.querySelector('[data-back]').hidden = name !== 'email' || this.signupVariant === 'direct';
      this.dialog.setAttribute('aria-labelledby', name === 'success' ? 'lsr-success-title' : 'lsr-title');
      this.dialog.setAttribute('aria-describedby', name === 'reveal' ? 'lsr-description' : name === 'email' ? 'lsr-consent' : '');
      if (name === 'email' && previousStep !== 'email' && this.dialog.open) this.track('luxsol_roof_email_step');
      if (focus) {
        const target = name === 'email' ? (this.signupVariant === 'direct' ? this.firstName : this.querySelector('[data-back]')) : name === 'reveal' ? this.slider : this.querySelector('[data-step="success"] button');
        target.focus({ preventScroll: true });
      }
    }
    updateRoof() {
      const value = Number(this.slider.value);
      this.roof.style.setProperty('--lsr-angle', value * .88 + 'deg');
      this.roof.style.setProperty('--lsr-open', Math.max(0, (value - 28) / 72));
      const sunlight = Math.max(0, (value - 12) / 88);
      this.roof.style.setProperty('--lsr-sun', sunlight * sunlight);
      this.roof.style.setProperty('--lsr-sun-shift', value * .48 - 24 + 'px');
      if (value >= 90 && !this.unlocked) {
        this.unlocked = true;
        this.track('luxsol_roof_unlock');
      }
      const label = this.unlocked ? this.status.dataset.unlocked : this.status.dataset.locked;
      this.status.textContent = label;
      this.slider.setAttribute('aria-valuetext', label + ', ' + value + '%');
      this.claim.disabled = !this.unlocked;
      if (this.unlocked) this.claim.textContent = this.claim.dataset.claimLabel + ' →';
    }
    async subscribe(event) {
      event.preventDefault();
      this.firstName.value = this.firstName.value.trim();
      if (this.busy || !this.unlocked || !this.form.reportValidity()) return;
      if (this.form.elements.company.value) return;
      this.error.hidden = true;
      if (this.preview) {
        this.showStep('success');
        this.firstName.value = '';
        this.email.value = '';
        return;
      }
      if (!this.dataset.siteId || !this.dataset.listId) {
        this.error.textContent = this.error.dataset.generic;
        this.error.hidden = false;
        return;
      }
      this.busy = true;
      this.track('luxsol_roof_submit');
      this.submit.disabled = true;
      this.submit.textContent = this.submit.dataset.busy;
      this.requestController = new AbortController();
      const timeout = setTimeout(() => this.requestController.abort(), 12000);
      const payload = {data:{type:'subscription',attributes:{custom_source:this.dataset.source,profile:{data:{type:'profile',attributes:{first_name:this.firstName.value,email:this.email.value.trim(),subscriptions:{email:{marketing:{consent:'SUBSCRIBED'}}},properties:{'LuxSol Signup Source':'Roof reveal','LuxSol Welcome Offer':'£100 off','LuxSol Signup Experiment':this.experimentId,'LuxSol Signup Variant':this.signupVariant}}}}},relationships:{list:{data:{type:'list',id:this.dataset.listId}}}}};
      try {
        const response = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(this.dataset.siteId), {
          method:'POST',headers:{'Content-Type':'application/vnd.api+json',Accept:'application/vnd.api+json',revision:'2026-07-15'},body:JSON.stringify(payload),signal:this.requestController.signal,credentials:'omit'
        });
        if (response.status !== 202) throw new Error(response.status === 429 ? 'rate' : 'signup');
        this.save('submitted', true);
        this.firstName.value = '';
        this.email.value = '';
        this.showStep('success');
        this.track('luxsol_roof_signup_success');
      } catch (error) {
        this.track('luxsol_roof_signup_error', { reason: error.message === 'rate' ? 'rate_limit' : 'other' });
        this.error.textContent = error.message === 'rate' ? this.error.dataset.rate : this.error.dataset.generic;
        this.error.hidden = false;
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        this.submit.disabled = false;
        this.submit.textContent = this.submit.dataset.idle;
      }
    }
  }
  customElements.define('luxsol-roof-signup', LuxSolRoofSignup);
})();
